import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import HubTopBar from "./HubTopBar";
import Footer from "./Footer";
import { Button } from "../ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import * as Icons from "lucide-react";
import { ArrowRight, Sparkles, BadgeCheck, Instagram, Search, Megaphone, Radio, Check } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { PRICING } from "../../data/pricing";
import { WHATSAPP_URL } from "../../config";

export default function SegmentPage({ translations, images }) {
  const { lang } = useLang();
  const t = translations[lang] || translations.en;
  const p = PRICING[lang] || PRICING.en;
  const [yearly, setYearly] = React.useState(false);
  const paidTiers = p.tiers.filter((x) => x.name !== "Free");
  const openWa = () => window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);

  const chipsMarquee = [...t.chips, ...t.chips];
  const mkChips = [
    { icon: Instagram, k: lang === "th" ? "\u0e42\u0e0b\u0e40\u0e0a\u0e35\u0e22\u0e25" : lang === "ru" ? "\u0421\u043e\u0446\u0441\u0435\u0442\u0438" : "Social campaigns" },
    { icon: Search, k: lang === "th" ? "\u0e04\u0e49\u0e19\u0e2b\u0e32" : lang === "ru" ? "\u041f\u043e\u0438\u0441\u043a" : "Search visibility" },
    { icon: Megaphone, k: lang === "th" ? "\u0e1a\u0e17\u0e04\u0e27\u0e32\u0e21" : lang === "ru" ? "\u0421\u0442\u0430\u0442\u044c\u0438" : "Editorial features" },
    { icon: Radio, k: lang === "th" ? "\u0e0a\u0e38\u0e21\u0e0a\u0e19" : lang === "ru" ? "\u0421\u043e\u043e\u0431\u0449\u0435\u0441\u0442\u0432\u043e" : "Community reach" },
  ];

  return (
    <div className="min-h-screen bg-[var(--aa-cream)] text-[var(--aa-ink)] font-body">
      <HubTopBar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--aa-wine)] text-[var(--aa-cream)]">
        <div className="absolute inset-0">
          <img src={images.hero} alt="" className="w-full h-full object-cover opacity-40 scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--aa-wine)]/85 via-[var(--aa-wine)]/70 to-[var(--aa-wine)]"></div>
        </div>
        <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border border-white/10"></div>
        <div className="pointer-events-none absolute -bottom-56 -left-40 w-[640px] h-[640px] rounded-full border border-white/5"></div>
        <div className="relative mx-auto max-w-6xl px-6 md:px-8 pt-20 md:pt-28 pb-24 md:pb-36 text-center">
          <div className="reveal in inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/25 bg-white/5 text-[11px] tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            {t.hero.eyebrow}
          </div>
          <h1 className="reveal font-display font-light text-[44px] leading-[1.05] sm:text-6xl md:text-[76px] md:leading-[1.02] mt-7 max-w-4xl mx-auto">
            {t.hero.h1a}
            <br />
            <span className="italic font-normal text-[var(--aa-cream)]/95">{t.hero.h1b}</span>
          </h1>
          <p className="reveal max-w-2xl mx-auto mt-7 text-[15px] md:text-[17px] leading-relaxed text-[var(--aa-cream)]/80">
            {t.hero.p}
          </p>
          <div className="reveal mt-10 inline-flex flex-col items-center gap-7">
            <a href="#fineprint" className="inline-flex items-center gap-3 pl-1.5 pr-6 py-1.5 rounded-full bg-[var(--aa-cream)] text-[var(--aa-wine)] hover:bg-white transition-colors shadow-[0_10px_40px_-15px_rgba(0,0,0,0.5)]">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--aa-wine)] text-[var(--aa-cream)]">
                <BadgeCheck className="w-4.5 h-4.5" strokeWidth={2.2} />
              </span>
              <span className="font-display italic text-xl md:text-2xl leading-none whitespace-nowrap">
                {t.hero.zero}{" "}
                <span className="text-[var(--aa-wine)]/55">
                  {t.hero.ever}<sup className="text-[11px] ml-0.5 not-italic">*</sup>
                </span>
              </span>
            </a>
            <Button onClick={openWa} className="group rounded-full bg-[var(--aa-cream)] hover:bg-white text-[var(--aa-wine)] px-7 py-6 text-[15px] font-semibold shadow-[0_10px_40px_-10px_rgba(0,0,0,0.4)]">
              {t.hero.cta}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <div className="text-xs text-[var(--aa-cream)]/60">{t.hero.tagline}</div>
          </div>
        </div>
      </section>

      {/* CHIPS */}
      <div className="relative -mt-8 md:-mt-10 z-10">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="rounded-full bg-[var(--aa-cream-warm)] border border-[var(--aa-line)] shadow-[0_20px_50px_-30px_rgba(107,15,16,0.35)] px-3 md:px-4 py-3 overflow-hidden">
            <div className="flex gap-3 marquee-track whitespace-nowrap">
              {chipsMarquee.map((c, i) => {
                const Ico = Icons[c.icon] || Icons.Sparkles;
                return (
                  <span key={i} className="inline-flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white border border-[var(--aa-line)] text-[13px] text-[var(--aa-wine)] font-medium flex-shrink-0">
                    <Ico className="w-4 h-4" />
                    {c.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* BENEFITS */}
      <section className="relative pt-14 pb-24 md:py-32 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="reveal max-w-3xl">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">{t.benefits.eyebrow}</div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-[var(--aa-ink)]">
              {t.benefits.h2a} <br className="hidden md:block" /><span className="italic">{t.benefits.h2b}</span>
            </h2>
            <p className="mt-6 text-[15px] md:text-lg text-[var(--aa-ink-soft)] max-w-2xl leading-relaxed">{t.benefits.p}</p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-5 md:gap-6">
            {t.benefits.items.map((b, i) => {
              const Ico = Icons[b.icon] || Icons.Star;
              return (
                <div key={i} className="reveal lift group relative rounded-3xl bg-white border border-[var(--aa-line)] p-7 md:p-9 overflow-hidden">
                  <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[var(--aa-cream-warm)] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[var(--aa-wine)] text-[var(--aa-cream)]"><Ico className="w-5 h-5" /></span>
                    <span className="text-xs tracking-[0.18em] uppercase text-[var(--aa-wine)]/70">0{i + 1}</span>
                  </div>
                  <h3 className="relative font-display text-2xl md:text-[28px] mt-6 text-[var(--aa-ink)] leading-tight">{b.title}</h3>
                  <p className="relative mt-3 text-[14.5px] text-[var(--aa-ink-soft)] leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
          <div className="reveal mt-16 md:mt-20 grid grid-cols-12 gap-4 md:gap-5">
            <div className="col-span-12 md:col-span-7 rounded-3xl overflow-hidden aspect-[16/10] relative">
              <img src={images.wine} alt="Wine and dining" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block col-span-6 md:col-span-5 rounded-3xl overflow-hidden aspect-[4/5] relative">
              <img src={images.coffee} alt="Coffee pour" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="relative py-24 md:py-32 bg-[var(--aa-wine)] text-[var(--aa-cream)] overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
          <div className="absolute -top-32 -left-24 w-[560px] h-[560px] rounded-full bg-[var(--aa-cream)]"></div>
          <div className="absolute -bottom-32 -right-24 w-[480px] h-[480px] rounded-full bg-[var(--aa-cream)]"></div>
        </div>
        <div className="relative mx-auto max-w-6xl px-6 md:px-8">
          <div className="reveal max-w-3xl">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-cream)]/70 font-semibold mb-4">{t.audience.eyebrow}</div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05]">
              {t.audience.h2a} <span className="italic">{t.audience.h2b}</span>
            </h2>
            <p className="mt-6 text-[15px] md:text-lg text-[var(--aa-cream)]/80 max-w-2xl leading-relaxed">{t.audience.p}</p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-5 md:gap-6">
            {t.audience.items.map((a, i) => (
              <div key={i} className="reveal lift relative rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-sm p-8 md:p-9">
                <div className="font-display text-5xl md:text-6xl italic text-[var(--aa-cream)]/25 leading-none">{a.tag}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-4">{a.heading}</h3>
                <p className="mt-3 text-[14.5px] text-[var(--aa-cream)]/75 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
          <div className="reveal mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {t.audience.stats.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/12 px-5 py-6 bg-white/[0.03]">
                <div className="font-display text-3xl md:text-4xl">{s.k}</div>
                <div className="text-xs mt-1 text-[var(--aa-cream)]/70 uppercase tracking-[0.16em]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETING */}
      <section className="relative pt-24 pb-16 md:py-32 bg-[var(--aa-cream-warm)]">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="grid md:grid-cols-2 gap-14 md:gap-16 items-center">
            <div className="reveal">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">{t.marketing.eyebrow}</div>
              <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-[var(--aa-ink)]">
                {t.marketing.h2a} <span className="italic">{t.marketing.h2b}</span>
              </h2>
              <p className="mt-6 text-[15px] md:text-lg text-[var(--aa-ink-soft)] leading-relaxed">{t.marketing.p}</p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {mkChips.map(({ icon: Ico, k }, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-2xl bg-white border border-[var(--aa-line)] px-4 py-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--aa-wine)]/8 text-[var(--aa-wine)]"><Ico className="w-4 h-4" /></span>
                    <div className="text-[13.5px] font-medium text-[var(--aa-ink)]">{k}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal relative">
              <div className="rounded-[28px] overflow-hidden aspect-[4/3] md:aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(107,15,16,0.35)]">
                <img src={images.bar} alt="Bar" className="w-full h-full object-cover object-[center_75%] md:object-center" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="relative pt-14 pb-24 md:py-32 bg-[var(--aa-cream-warm)]">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="reveal max-w-3xl">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">{t.steps.eyebrow}</div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-[var(--aa-ink)]">
              {t.steps.h2a} <span className="italic">{t.steps.h2b}</span>
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-5 md:gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[16%] right-[16%] h-px bg-[var(--aa-line)]"></div>
            {t.steps.items.map((s, i) => (
              <div key={i} className="reveal lift relative rounded-3xl bg-white border border-[var(--aa-line)] p-8">
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--aa-wine)] text-[var(--aa-cream)]">
                  <span className="font-display text-3xl italic">{s.n}</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl mt-6 text-[var(--aa-ink)]">{s.title}</h3>
                <p className="mt-3 text-[14.5px] text-[var(--aa-ink-soft)] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="reveal text-center mt-12">
            <Link to="/pricing" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--aa-wine)] underline underline-offset-4">
              See plans and pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="plan" className="relative py-24 md:py-32 bg-[var(--aa-wine)] text-[var(--aa-cream)] overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-white/10"></div>
        <div className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full border border-white/5"></div>
        <div className="relative mx-auto max-w-6xl px-6 md:px-8">
          <div className="reveal text-center">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-cream)]/70 font-semibold mb-4">
              {p.hero.eyebrow}
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05]">
              {lang === "th" ? "\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e41\u0e1e\u0e04\u0e40\u0e01\u0e08 " : lang === "ru" ? "\u0412\u044b\u0431\u0435\u0440\u0438\u0442\u0435 " : "Pick your "}
              <span className="italic">{lang === "th" ? "\u0e02\u0e2d\u0e07\u0e04\u0e38\u0e13" : lang === "ru" ? "\u0442\u0430\u0440\u0438\u0444" : "plan"}.</span>
            </h2>
            <div className="reveal mt-8 inline-flex items-center gap-1 p-1 rounded-full border border-white/20 bg-white/5">
              <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors ${!yearly ? "bg-[var(--aa-cream)] text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/80"}`}>
                {p.toggle.monthly}
              </button>
              <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors flex items-center gap-1.5 ${yearly ? "bg-[var(--aa-cream)] text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/80"}`}>
                {p.toggle.yearly}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--aa-gold)]/20 text-[var(--aa-gold)] font-semibold">
                  {p.toggle.saveBadge}
                </span>
              </button>
            </div>
          </div>

          <div className="reveal grid md:grid-cols-3 gap-5 md:gap-6 mt-14">
            {paidTiers.map((tier) => {
              const rec = tier.popular;
              const price = yearly ? tier.priceY : tier.priceM;
              const per = yearly ? tier.perY : tier.perM;
              return (
                <div key={tier.name} className={`relative rounded-3xl p-8 md:p-9 border transition-all duration-300 flex flex-col ${rec ? "bg-[var(--aa-cream)] text-[var(--aa-ink)] border-transparent shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]" : "bg-white/[0.04] border-white/15 backdrop-blur-sm"}`}>
                  {rec && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--aa-wine)] text-[var(--aa-cream)] text-[11px] font-semibold tracking-wide">
                      <Sparkles className="w-3 h-3" /> {p.popular}
                    </div>
                  )}
                  <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${rec ? "text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/70"}`}>{tier.name}</div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-display text-5xl md:text-6xl">{price}</span>
                    <span className={`text-sm ${rec ? "text-[var(--aa-ink-soft)]" : "text-[var(--aa-cream)]/70"}`}>/mo</span>
                  </div>
                  <div className={`text-xs mt-1 ${rec ? "text-[var(--aa-ink-soft)]" : "text-[var(--aa-cream)]/60"}`}>{per}</div>
                  <p className={`mt-4 text-[13.5px] leading-relaxed ${rec ? "text-[var(--aa-ink-soft)]" : "text-[var(--aa-cream)]/80"}`}>{tier.forText}</p>
                  {tier.inheritKey && (
                    <div className={`mt-5 text-[11px] uppercase tracking-[0.18em] font-semibold ${rec ? "text-[var(--aa-wine)]" : "text-[var(--aa-cream)]/70"}`}>
                      {p.inherit[tier.inheritKey]}
                    </div>
                  )}
                  <ul className="mt-4 space-y-2.5 flex-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2.5 text-[13.5px] ${f.note ? "pl-7 -mt-1.5 opacity-75" : ""}`}>
                        {!f.note && (
                          <span className={`mt-0.5 inline-flex w-[18px] h-[18px] rounded-full items-center justify-center flex-shrink-0 ${rec ? "bg-[var(--aa-wine)] text-[var(--aa-cream)]" : "bg-white/15 text-[var(--aa-cream)]"}`}>
                            <Check className="w-3 h-3" strokeWidth={2.6} />
                          </span>
                        )}
                        <span className={rec ? "text-[var(--aa-ink)]" : "text-[var(--aa-cream)]/85"}>{f.t}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={openWa} className={`w-full mt-8 rounded-full py-6 font-semibold text-[14px] group ${rec ? "bg-[var(--aa-wine)] hover:bg-[var(--aa-wine-deep)] text-[var(--aa-cream)]" : "bg-[var(--aa-cream)] hover:bg-white text-[var(--aa-wine)]"}`}>
                    {tier.cta}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10 text-[13px] text-[var(--aa-cream)]/70">
            <Link to="/pricing" className="underline underline-offset-4 hover:text-[var(--aa-cream)]">
              {lang === "th" ? "\u0e14\u0e39\u0e41\u0e1e\u0e04\u0e40\u0e01\u0e08\u0e17\u0e31\u0e49\u0e07\u0e2b\u0e21\u0e14\u0e41\u0e25\u0e30\u0e40\u0e1b\u0e23\u0e35\u0e22\u0e1a\u0e40\u0e17\u0e35\u0e22\u0e1a" : lang === "ru" ? "\u0421\u043c\u043e\u0442\u0440\u0435\u0442\u044c \u0432\u0441\u0435 \u0442\u0430\u0440\u0438\u0444\u044b" : "See all plans and compare features"}
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-32 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-4xl px-6 md:px-8">
          <div className="reveal text-center">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">{t.faq.eyebrow}</div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-[var(--aa-ink)]">
              {t.faq.h2a} <span className="italic">{t.faq.h2b}</span>
            </h2>
          </div>
          <div className="reveal mt-12">
            <Accordion type="single" collapsible className="space-y-3">
              {t.faq.items.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-[var(--aa-line)] bg-white px-5 md:px-6 data-[state=open]:shadow-sm">
                  <AccordionTrigger className="text-left font-display text-lg md:text-xl text-[var(--aa-ink)] hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[14.5px] text-[var(--aa-ink-soft)] leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
